import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { canOrg, type Project } from '@allemnii-pulse/shared';
import { useOrgProjects } from '../../lib/useOrgProjects.js';
import { useOrgs } from '../orgs/OrgProvider.js';
import { Card } from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { NewProjectDialog } from './NewProjectDialog.js';

export function ProjectsListPage() {
  const { currentOrgId, currentOrg } = useOrgs();
  const canCreate = canOrg(currentOrg?.role, 'org.project.create');
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data: projects, loading } = useOrgProjects();
  const activeProjects = useMemo(() => projects.filter((p) => p.status !== 'archived'), [projects]);
  const archivedProjects = useMemo(
    () => projects.filter((p) => p.status === 'archived'),
    [projects],
  );

  return (
    <div className="flex flex-col gap-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-lg font-semibold">Projects</h1>
          <p className="text-sm text-muted">Every project in this organisation.</p>
        </div>
        {canCreate && (
          <Button variant="default" className="ml-auto" onClick={() => setDialogOpen(true)}>
            New project
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : projects.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted">
          No projects yet. Create one to start the PRD → TDD → Tasks flow.
        </Card>
      ) : (
        <>
          {archivedProjects.length > 0 && (
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-muted">Active</h2>
              <span className="rounded-full bg-surface-2 px-2 py-0.5 text-xs text-muted">
                {activeProjects.length}
              </span>
            </div>
          )}
          <ProjectGrid projects={activeProjects} emptyLabel="No active projects." />

          {archivedProjects.length > 0 && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-muted">Archived</h2>
                <span className="rounded-full bg-surface-2 px-2 py-0.5 text-xs text-muted">
                  {archivedProjects.length}
                </span>
              </div>
              <ProjectGrid projects={archivedProjects} muted />
            </div>
          )}
        </>
      )}

      {currentOrgId && (
        <NewProjectDialog
          orgId={currentOrgId}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </div>
  );
}

function ProjectGrid({
  projects,
  muted = false,
  emptyLabel,
}: {
  projects: Project[];
  muted?: boolean;
  emptyLabel?: string;
}) {
  if (projects.length === 0) {
    return emptyLabel ? <p className="text-sm text-muted">{emptyLabel}</p> : null;
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {projects.map((p, i) => (
        <motion.div
          key={p.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, delay: i * 0.03 }}
        >
          <Link to={`/projects/${p.id}`}>
            <Card
              className={`flex flex-col gap-2 p-4 transition-colors hover:border-[var(--ink-400)] ${
                muted ? 'opacity-70' : ''
              }`}
            >
              <span className="break-words font-medium">{p.name}</span>
              <span className="text-xs text-muted">
                {p.startDate}
                {p.endDate ? ` → ${p.endDate}` : ''}
              </span>
            </Card>
          </Link>
        </motion.div>
      ))}
    </div>
  );
}
